// src/modules/communications/pages/PlaceholderLeadsPage.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaUser, 
  FaQuestionCircle, 
  FaPhone, 
  FaSms, 
  FaSpinner,
  FaCalendarAlt,
  FaArrowRight,
  FaPlus,
  FaSearch,
  FaTimes,
  FaSort,
  FaSortAmountDown,
  FaSortAmountUp
} from 'react-icons/fa';
import { getPlaceholderLeads } from '../services/communicationService';
import DashboardLayout from '../../../layouts/DashboardLayout';
import { formatPhoneNumber, formatDate } from '../utils/formatUtils';
import { useCommunication } from '../context/CommunicationContext';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../components/shared/Pagination';

// Styled Components
const PageContainer = styled.div`
  padding: ${props => props.theme.spacing(2)};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const Description = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const FiltersContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(3)};
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(2)};
  align-items: center;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0 ${props => props.theme.spacing(1)};
  flex-grow: 1;
  max-width: 300px;
  
  input {
    flex-grow: 1;
    padding: ${props => props.theme.spacing(1)};
    background: transparent;
    border: none;
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.typography.fontSize.small};
    
    &:focus {
      outline: none;
    }
  }
  
  svg {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  background: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.isActive ? '#fff' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:hover {
    background: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.primary + '22'};
    border-color: ${props => props.theme.colors.primary};
  }
  
  svg {
    font-size: 0.9rem;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:hover {
    text-decoration: underline;
  }
`;

const SummaryBox = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(3)};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  .summary-count {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.primary};
  }
`;

const LeadsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing(3)};
`;

const LeadCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const LeadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const LeadName = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  
  svg {
    color: ${props => props.theme.colors.warning};
  }
`;

const PlaceholderBadge = styled.span`
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${props => props.theme.colors.warning}33;
  color: ${props => props.theme.colors.warning};
  font-weight: normal;
`;

const LeadPhone = styled.div`
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const LeadStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(3)};
  margin-bottom: ${props => props.theme.spacing(2)};
  
  .stat {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.colors.textSecondary};
    font-size: ${props => props.theme.typography.fontSize.small};
    
    svg {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const LeadDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const LeadActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(3)};
  
  button {
    flex: 1;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  background: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.primary ? '#fff' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.primary + '22'};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(6)};
  text-align: center;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  grid-column: 1 / -1;
  
  svg {
    font-size: 3rem;
    color: ${props => props.theme.colors.inputGray};
    margin-bottom: ${props => props.theme.spacing(2)};
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const PaginationContainer = styled.div`
  margin-top: ${props => props.theme.spacing(3)};
  display: flex;
  justify-content: center;
`;

/**
 * Placeholder Leads Page - displays all unknown contacts (placeholder leads)
 * @returns {React.ReactElement} Placeholder Leads Page component
 */
const PlaceholderLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'activity'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'
  
  const { openCallModal, openSmsComposer } = useCommunication();
  const navigate = useNavigate();
  
  // Fetch placeholder leads
  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const data = await getPlaceholderLeads(
          pagination.currentPage,
          10,
          searchQuery,
          sortBy,
          sortDirection
        );
        
        setLeads(data.placeholderLeads);
        setPagination({
          currentPage: data.currentPage,
          totalPages: data.totalPages,
          totalItems: data.totalItems
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching placeholder leads:', err);
        setError('Failed to load unknown contacts');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeads();
  }, [pagination.currentPage, searchQuery, sortBy, sortDirection]);
  
  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    // Scroll to top
    window.scrollTo(0, 0);
  };
  
  // Handle lead click
  const handleLeadClick = (leadId) => {
    navigate(`/communications/placeholder-leads/${leadId}`);
  };
  
  // Handle action buttons
  const handleCall = (e, phoneNumber, leadId) => {
    e.stopPropagation(); // Prevent card click
    openCallModal(phoneNumber, leadId);
  };
  
  const handleSms = (e, phoneNumber, leadId) => {
    e.stopPropagation(); // Prevent card click
    openSmsComposer(phoneNumber, leadId);
  };
  
  const handleUpgrade = (e, leadId) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/communications/placeholder-leads/${leadId}`);
  };
  
  // Handle search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };
  
  // Handle sort
  const handleSortChange = (type) => {
    if (sortBy === type) {
      // Toggle direction if same type
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Change type and set direction to desc
      setSortBy(type);
      setSortDirection('desc');
    }
  };
  
  return (
    <DashboardLayout>
      <PageContainer>
        <PageHeader>
          <Title>Unknown Contacts</Title>
        </PageHeader>
        
        <Description>
          These are placeholder leads created from communications with unknown numbers.
          You can upgrade them to real leads by adding customer information.
        </Description>
        
        {/* Filters */}
        <FiltersContainer>
          <SearchBox>
            <FaSearch />
            <input 
              type="text" 
              placeholder="Search phone number"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <FaTimes 
                style={{ cursor: 'pointer' }} 
                onClick={clearSearch} 
              />
            )}
          </SearchBox>
          
          <SortButton 
            isActive={sortBy === 'date'}
            onClick={() => handleSortChange('date')}
          >
            <FaCalendarAlt />
            Date
            {sortBy === 'date' && (
              sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
            )}
          </SortButton>
          
          <SortButton 
            isActive={sortBy === 'activity'}
            onClick={() => handleSortChange('activity')}
          >
            <FaPhone />
            Activity
            {sortBy === 'activity' && (
              sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
            )}
          </SortButton>
        </FiltersContainer>
        
        {/* Summary */}
        {pagination.totalItems > 0 && (
          <SummaryBox>
            Showing <span className="summary-count">{leads.length}</span> of{' '}
            <span className="summary-count">{pagination.totalItems}</span> unknown contacts
            {searchQuery && ` • Search: "${searchQuery}"`}
          </SummaryBox>
        )}
        
        {/* Leads List */}
        {loading && leads.length === 0 ? (
          <LoadingContainer>
            <FaSpinner />
          </LoadingContainer>
        ) : leads.length === 0 ? (
          <EmptyState>
            <FaQuestionCircle />
            <p>No unknown contacts found matching your criteria.</p>
          </EmptyState>
        ) : (
          <LeadsList>
            {leads.map((lead) => (
              <LeadCard 
                key={lead.id} 
                onClick={() => handleLeadClick(lead.id)}
              >
                <LeadHeader>
                  <LeadName>
                    <FaQuestionCircle />
                    {lead.name}
                  </LeadName>
                  <PlaceholderBadge>Unknown</PlaceholderBadge>
                </LeadHeader>
                
                <LeadPhone>
                  {formatPhoneNumber(lead.phone)}
                </LeadPhone>
                
                <LeadStats>
                  <div className="stat">
                    <FaPhone />
                    {lead.callCount} calls
                  </div>
                  <div className="stat">
                    <FaSms />
                    {lead.messageCount} messages
                  </div>
                </LeadStats>
                
                <LeadDate>
                  <FaCalendarAlt />
                  First contact: {formatDate(lead.createdAt)}
                </LeadDate>
                
                <LeadActions>
                  <ActionButton
                    onClick={(e) => handleUpgrade(e, lead.id)}
                    primary
                  >
                    <FaPlus /> Convert to Customer
                  </ActionButton>
                  
                  <ActionButton
                    onClick={(e) => handleCall(e, lead.phone, lead.id)}
                  >
                    <FaPhone />
                  </ActionButton>
                  
                  <ActionButton
                    onClick={(e) => handleSms(e, lead.phone, lead.id)}
                  >
                    <FaSms />
                  </ActionButton>
                </LeadActions>
              </LeadCard>
            ))}
          </LeadsList>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <PaginationContainer>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </PaginationContainer>
        )}
      </PageContainer>
    </DashboardLayout>
  );
};

export default PlaceholderLeadsPage;